import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from './firebase';
import { STORAGE_KEYS } from '../utils/constants';

/**
 * SafeLine Database Service
 * Provides real-time database operations using Firestore
 */

// Collection names
const COLLECTIONS = {
  SCHOOLS: 'schools',
  USERS: 'users',
  ROLES_PERMISSIONS: 'roles_permissions',
  CLASSES: 'classes',
  DEVICES: 'devices',
  TEACHER_SESSIONS: 'teacher_sessions',
  GUARDIANS: 'guardians',
  STUDENTS: 'students',
  GUESTS: 'guests',
  LICENSE_PLATES: 'license_plates',
  LICENSE_IMAGES: 'license_images',
  GROUPS: 'groups',
  PICKUP_LOGS: 'pickup_logs',
  AUDIT_LOGS: 'audit_logs',
  DAILYLOGS: 'dailylogs',
  STATS: 'stats',
  INVITATION_CODES: 'invitation_codes',
  CONTACTS: 'contacts',
  WALKING_SCHEDULES: 'walking_schedules',
  GUARDIAN_TAGS: 'guardian_tags',
  SETTINGS: 'settings'
};

// Helper function to get current user's school context
const getCurrentSchoolId = () => {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (userData) {
      const user = JSON.parse(userData);
      const schoolId = user.school_id || user.school_ids?.[0];
      console.log('Database: Current school ID:', schoolId);
      return schoolId;
    }
  } catch (error) {
    console.error('Database: Error getting current school ID:', error);
  }
  console.log('Database: No school ID found, returning null');
  return null;
};

// Helper function to get current user ID
const getCurrentUserId = () => {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (userData) {
      const user = JSON.parse(userData);
      return user.id;
    }
  } catch (error) {
    console.error('Database: Error getting current user ID:', error);
  }
  return null;
};

// Schools Service
export const schoolsService = {
  async getAll() {
    const snapshot = await getDocs(collection(db, COLLECTIONS.SCHOOLS));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getById(schoolId) {
    const docRef = doc(db, COLLECTIONS.SCHOOLS, schoolId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  },

  async create(schoolData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.SCHOOLS), {
      ...schoolData,
      created_at: serverTimestamp()
    });
    return docRef.id;
  },

  async update(schoolId, schoolData) {
    const docRef = doc(db, COLLECTIONS.SCHOOLS, schoolId);
    await updateDoc(docRef, {
      ...schoolData,
      updated_at: serverTimestamp()
    });
  },

  async delete(schoolId) {
    const docRef = doc(db, COLLECTIONS.SCHOOLS, schoolId);
    await deleteDoc(docRef);
  }
};

// Users Service
export const usersService = {
  async getAll(schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const currentUser = getCurrentUserId();
    console.log('Database: Getting all users, schoolId filter:', schoolIdToUse, 'current user:', currentUser);
    
    // Check if current user is a developer (has access to all schools)
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    let userRole = null;
    if (userData) {
      try {
        const user = JSON.parse(userData);
        userRole = user.role;
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    
    let q;
    if (userRole === 'developer' || !schoolIdToUse) {
      // Developer gets all users, or fallback to all users if no school ID
      console.log('Database: Developer access or no school filter - getting all users');
      q = collection(db, COLLECTIONS.USERS);
    } else {
      // Other roles get filtered by school
      console.log('Database: Filtering by school_id:', schoolIdToUse);
      q = query(collection(db, COLLECTIONS.USERS), where('school_id', '==', schoolIdToUse));
    }
    
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('Database: Found', users.length, 'users for role:', userRole);
    return users;
  },

  async getById(userId) {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  },

  async getByFirebaseUid(firebaseUid) {
    console.log('Database: Searching for user with firebase_uid:', firebaseUid);
    const q = query(collection(db, COLLECTIONS.USERS), where('firebase_uid', '==', firebaseUid));
    const snapshot = await getDocs(q);
    console.log('Database: Query results - found', snapshot.docs.length, 'documents');
    
    if (snapshot.docs.length > 0) {
      const userData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      console.log('Database: Found user data:', userData);
      return userData;
    } else {
      console.log('Database: No user found with firebase_uid:', firebaseUid);
      return null;
    }
  },

  async getByEmail(email) {
    console.log('Database: Searching for user with email:', email);
    const q = query(collection(db, COLLECTIONS.USERS), where('email', '==', email));
    const snapshot = await getDocs(q);
    console.log('Database: Email query results - found', snapshot.docs.length, 'documents');
    
    if (snapshot.docs.length > 0) {
      const userData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      console.log('Database: Found user data by email:', userData);
      return userData;
    } else {
      console.log('Database: No user found with email:', email);
      return null;
    }
  },

  async updateFirebaseUid(userId, newFirebaseUid) {
    console.log('Database: Updating firebase_uid for user:', userId, 'to:', newFirebaseUid);
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(docRef, {
      firebase_uid: newFirebaseUid,
      updated_at: serverTimestamp()
    });
    console.log('Database: Firebase UID updated successfully');
  },

  async create(userData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.USERS), {
      ...userData,
      created_at: serverTimestamp()
    });
    return docRef.id;
  },

  async update(userId, userData) {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(docRef, {
      ...userData,
      updated_at: serverTimestamp()
    });
  },

  async delete(userId) {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    await deleteDoc(docRef);
  }
};

// Students Service
export const studentsService = {
  async getAll(schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    
    // Check user role for permissions
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    let userRole = null;
    if (userData) {
      try {
        const user = JSON.parse(userData);
        userRole = user.role;
      } catch (e) {
        console.error('Error parsing user data for students:', e);
      }
    }
    
    let q;
    if (userRole === 'developer') {
      // Developer gets all students from all schools
      console.log('Database: Developer access - getting all students');
      q = collection(db, COLLECTIONS.STUDENTS);
    } else if (schoolIdToUse) {
      // Other roles get filtered by school
      console.log('Database: Getting students for school:', schoolIdToUse);
      q = query(
        collection(db, COLLECTIONS.STUDENTS),
        where('school_id', '==', schoolIdToUse)
      );
    } else {
      // No school ID and not developer - return empty array
      console.log('Database: No school ID available for non-developer user');
      return [];
    }
    
    console.log('Database: About to execute students query');
    try {
      const snapshot = await getDocs(q);
      console.log('Database: Students query executed, found', snapshot.docs.length, 'documents');
      
      if (snapshot.docs.length > 0) {
        console.log('Database: First student sample:', snapshot.docs[0].data());
      }
      
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Database: Returning', results.length, 'students');
      return results;
    } catch (error) {
      console.error('Database: Error executing students query:', error);
      throw error;
    }
  },

  async getById(studentId) {
    const docRef = doc(db, COLLECTIONS.STUDENTS, studentId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  },

  async getByClass(classId) {
    const q = query(
      collection(db, COLLECTIONS.STUDENTS),
      where('class_id', '==', classId),
      orderBy('name')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async create(studentData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.STUDENTS), {
      ...studentData,
      school_id: studentData.school_id || getCurrentSchoolId(),
      created_by_admin_id: getCurrentUserId(),
      created_at: serverTimestamp()
    });
    return docRef.id;
  },

  async update(studentId, studentData) {
    const docRef = doc(db, COLLECTIONS.STUDENTS, studentId);
    await updateDoc(docRef, {
      ...studentData,
      updated_at: serverTimestamp()
    });
  },

  async delete(studentId) {
    const docRef = doc(db, COLLECTIONS.STUDENTS, studentId);
    await deleteDoc(docRef);
  },

  async updateWalkingStatus(studentId, isWalking, walkingScheduleId = null) {
    const docRef = doc(db, COLLECTIONS.STUDENTS, studentId);
    await updateDoc(docRef, {
      is_walking_today: isWalking,
      walking_schedule_id: walkingScheduleId,
      updated_at: serverTimestamp()
    });
  }
};

// Guardians Service
export const guardiansService = {
  async getAll(schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    
    // Check user role for permissions
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    let userRole = null;
    if (userData) {
      try {
        const user = JSON.parse(userData);
        userRole = user.role;
      } catch (e) {
        console.error('Error parsing user data for guardians:', e);
      }
    }
    
    let q;
    if (userRole === 'developer') {
      // Developer gets all guardians from all schools
      console.log('Database: Developer access - getting all guardians');
      q = collection(db, COLLECTIONS.GUARDIANS);
    } else if (schoolIdToUse) {
      // Other roles get filtered by school
      console.log('Database: Getting guardians for school:', schoolIdToUse);
      q = query(
        collection(db, COLLECTIONS.GUARDIANS),
        where('school_id', '==', schoolIdToUse)
      );
    } else {
      // No school ID and not developer - return empty array
      console.log('Database: No school ID available for non-developer user');
      return [];
    }
    
    console.log('Database: About to execute guardians query');
    try {
      const snapshot = await getDocs(q);
      console.log('Database: Guardians query executed, found', snapshot.docs.length, 'documents');
      
      if (snapshot.docs.length > 0) {
        console.log('Database: First guardian sample:', snapshot.docs[0].data());
      }
      
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Database: Returning', results.length, 'guardians');
      return results;
    } catch (error) {
      console.error('Database: Error executing guardians query:', error);
      throw error;
    }
  },

  async getById(guardianId) {
    const docRef = doc(db, COLLECTIONS.GUARDIANS, guardianId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  },

  async getPendingApproval(schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const q = query(
      collection(db, COLLECTIONS.GUARDIANS),
      where('school_id', '==', schoolIdToUse),
      where('verification_status', '==', 'pending'),
      orderBy('created_at')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async create(guardianData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.GUARDIANS), {
      ...guardianData,
      school_id: guardianData.school_id || getCurrentSchoolId(),
      created_at: serverTimestamp()
    });
    return docRef.id;
  },

  async update(guardianId, guardianData) {
    const docRef = doc(db, COLLECTIONS.GUARDIANS, guardianId);
    await updateDoc(docRef, {
      ...guardianData,
      updated_at: serverTimestamp()
    });
  },

  async approve(guardianId) {
    const docRef = doc(db, COLLECTIONS.GUARDIANS, guardianId);
    await updateDoc(docRef, {
      verification_status: 'approved',
      is_authorized: true,
      is_verified: true,
      verified_by_user_id: getCurrentUserId(),
      updated_at: serverTimestamp()
    });
  },

  async deny(guardianId) {
    const docRef = doc(db, COLLECTIONS.GUARDIANS, guardianId);
    await updateDoc(docRef, {
      verification_status: 'denied',
      is_authorized: false,
      verified_by_user_id: getCurrentUserId(),
      updated_at: serverTimestamp()
    });
  },

  async delete(guardianId) {
    const docRef = doc(db, COLLECTIONS.GUARDIANS, guardianId);
    await deleteDoc(docRef);
  }
};

// Guests Service
export const guestsService = {
  async getAll(schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    console.log('Database: Getting guests for school:', schoolIdToUse);
    
    const q = query(
      collection(db, COLLECTIONS.GUESTS),
      where('school_id', '==', schoolIdToUse)
    );
    
    console.log('Database: About to execute guests query');
    try {
      const snapshot = await getDocs(q);
      console.log('Database: Guests query executed, found', snapshot.docs.length, 'documents');
      
      if (snapshot.docs.length > 0) {
        console.log('Database: First guest sample:', snapshot.docs[0].data());
      }
      
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Database: Returning', results.length, 'guests');
      return results;
    } catch (error) {
      console.error('Database: Error executing guests query:', error);
      throw error;
    }
  },

  async getById(guestId) {
    const docRef = doc(db, COLLECTIONS.GUESTS, guestId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  },

  async getPendingApproval(schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const q = query(
      collection(db, COLLECTIONS.GUESTS),
      where('school_id', '==', schoolIdToUse),
      where('approval_status', '==', 'pending'),
      orderBy('created_at')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async create(guestData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.GUESTS), {
      ...guestData,
      school_id: guestData.school_id || getCurrentSchoolId(),
      created_at: serverTimestamp()
    });
    return docRef.id;
  },

  async update(guestId, guestData) {
    const docRef = doc(db, COLLECTIONS.GUESTS, guestId);
    await updateDoc(docRef, {
      ...guestData,
      updated_at: serverTimestamp()
    });
  },

  async approve(guestId) {
    const docRef = doc(db, COLLECTIONS.GUESTS, guestId);
    await updateDoc(docRef, {
      approval_status: 'approved',
      authorized: true,
      approved_by_user_id: getCurrentUserId(),
      updated_at: serverTimestamp()
    });
  },

  async deny(guestId) {
    const docRef = doc(db, COLLECTIONS.GUESTS, guestId);
    await updateDoc(docRef, {
      approval_status: 'denied',
      authorized: false,
      approved_by_user_id: getCurrentUserId(),
      updated_at: serverTimestamp()
    });
  },

  async delete(guestId) {
    const docRef = doc(db, COLLECTIONS.GUESTS, guestId);
    await deleteDoc(docRef);
  }
};

// Pickup Logs Service
export const pickupLogsService = {
  async getAll(schoolId = null, limitCount = 50) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    
    // Check user role for permissions
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    let userRole = null;
    if (userData) {
      try {
        const user = JSON.parse(userData);
        userRole = user.role;
      } catch (e) {
        console.error('Error parsing user data for pickup logs:', e);
      }
    }
    
    let q;
    if (userRole === 'developer') {
      // Developer gets all pickup logs from all schools
      console.log('Database: Developer access - getting all pickup logs');
      q = query(
        collection(db, COLLECTIONS.PICKUP_LOGS),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    } else if (schoolIdToUse) {
      // Other roles get filtered by school
      console.log('Database: Getting pickup logs for school:', schoolIdToUse);
      q = query(
        collection(db, COLLECTIONS.PICKUP_LOGS),
        where('school_id', '==', schoolIdToUse),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
    } else {
      // No school ID and not developer - return empty array
      console.log('Database: No school ID available for non-developer user');
      return [];
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getByStudent(studentId) {
    const q = query(
      collection(db, COLLECTIONS.PICKUP_LOGS),
      where('student_id', '==', studentId),
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getByDateRange(startDate, endDate, schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const q = query(
      collection(db, COLLECTIONS.PICKUP_LOGS),
      where('school_id', '==', schoolIdToUse),
      where('timestamp', '>=', startDate),
      where('timestamp', '<=', endDate),
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async search(searchParams) {
    const { studentName, licenseId, guardianName, method, schoolId } = searchParams;
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    
    // Check user role for permissions
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    let userRole = null;
    if (userData) {
      try {
        const user = JSON.parse(userData);
        userRole = user.role;
      } catch (e) {
        console.error('Error parsing user data for pickup search:', e);
      }
    }
    
    let q;
    if (userRole === 'developer') {
      // Developer searches all schools
      console.log('Database: Developer search - all pickup logs');
      q = query(
        collection(db, COLLECTIONS.PICKUP_LOGS),
        orderBy('timestamp', 'desc')
      );
    } else if (schoolIdToUse) {
      // Other roles search their school
      console.log('Database: Searching pickup logs for school:', schoolIdToUse);
      q = query(
        collection(db, COLLECTIONS.PICKUP_LOGS),
        where('school_id', '==', schoolIdToUse),
        orderBy('timestamp', 'desc')
      );
    } else {
      // No school ID and not developer - return empty array
      console.log('Database: No school ID available for pickup search');
      return [];
    }
    
    if (method) {
      q = query(q, where('pickup_method', '==', method));
    }
    
    const snapshot = await getDocs(q);
    let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Client-side filtering for text searches
    if (studentName) {
      results = results.filter(log => 
        log.student_name?.toLowerCase().includes(studentName.toLowerCase())
      );
    }
    
    if (licenseId) {
      results = results.filter(log => 
        log.license_plate_id?.toLowerCase().includes(licenseId.toLowerCase())
      );
    }
    
    if (guardianName) {
      results = results.filter(log => 
        log.guardian_name?.toLowerCase().includes(guardianName.toLowerCase())
      );
    }
    
    return results;
  },

  async create(pickupData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.PICKUP_LOGS), {
      ...pickupData,
      school_id: pickupData.school_id || getCurrentSchoolId(),
      timestamp: serverTimestamp()
    });
    return docRef.id;
  }
};

// Walking Schedules Service
export const walkingSchedulesService = {
  async getAll(schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const q = query(
      collection(db, COLLECTIONS.WALKING_SCHEDULES),
      where('school_id', '==', schoolIdToUse),
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getByStudent(studentId) {
    const q = query(
      collection(db, COLLECTIONS.WALKING_SCHEDULES),
      where('student_id', '==', studentId),
      where('is_active', '==', true),
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getByGuardian(guardianId) {
    const q = query(
      collection(db, COLLECTIONS.WALKING_SCHEDULES),
      where('guardian_id', '==', guardianId),
      where('is_active', '==', true),
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async create(scheduleData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.WALKING_SCHEDULES), {
      ...scheduleData,
      school_id: scheduleData.school_id || getCurrentSchoolId(),
      created_by_guardian_id: getCurrentUserId(),
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return docRef.id;
  },

  async update(scheduleId, scheduleData) {
    const docRef = doc(db, COLLECTIONS.WALKING_SCHEDULES, scheduleId);
    await updateDoc(docRef, {
      ...scheduleData,
      updated_at: serverTimestamp()
    });
  },

  async approve(scheduleId) {
    const docRef = doc(db, COLLECTIONS.WALKING_SCHEDULES, scheduleId);
    await updateDoc(docRef, {
      approval_status: 'approved',
      approved_by_user_id: getCurrentUserId(),
      updated_at: serverTimestamp()
    });
  },

  async deny(scheduleId) {
    const docRef = doc(db, COLLECTIONS.WALKING_SCHEDULES, scheduleId);
    await updateDoc(docRef, {
      approval_status: 'denied',
      approved_by_user_id: getCurrentUserId(),
      updated_at: serverTimestamp()
    });
  },

  async delete(scheduleId) {
    const docRef = doc(db, COLLECTIONS.WALKING_SCHEDULES, scheduleId);
    await deleteDoc(docRef);
  }
};

// Guardian Tags Service
export const guardianTagsService = {
  async getAll(schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const q = query(
      collection(db, COLLECTIONS.GUARDIAN_TAGS),
      where('school_id', '==', schoolIdToUse),
      orderBy('tag_number')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getByGuardian(guardianId) {
    const q = query(
      collection(db, COLLECTIONS.GUARDIAN_TAGS),
      where('guardian_id', '==', guardianId),
      where('is_active', '==', true),
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async create(tagData) {
    const docRef = await addDoc(collection(db, COLLECTIONS.GUARDIAN_TAGS), {
      ...tagData,
      school_id: tagData.school_id || getCurrentSchoolId(),
      assigned_by_user_id: getCurrentUserId(),
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    });
    return docRef.id;
  },

  async update(tagId, tagData) {
    const docRef = doc(db, COLLECTIONS.GUARDIAN_TAGS, tagId);
    await updateDoc(docRef, {
      ...tagData,
      updated_at: serverTimestamp()
    });
  },

  async deactivate(tagId) {
    const docRef = doc(db, COLLECTIONS.GUARDIAN_TAGS, tagId);
    await updateDoc(docRef, {
      is_active: false,
      updated_at: serverTimestamp()
    });
  },

  async delete(tagId) {
    const docRef = doc(db, COLLECTIONS.GUARDIAN_TAGS, tagId);
    await deleteDoc(docRef);
  }
};

// Stats Service
export const statsService = {
  async getDailyStats(date, schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const statsId = `${schoolIdToUse}_${date}`;
    
    const docRef = doc(db, COLLECTIONS.STATS, statsId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  },

  async getWeeklyStats(weekStart, schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const statsId = `${schoolIdToUse}_${weekStart}_week`;
    
    const docRef = doc(db, COLLECTIONS.STATS, statsId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  },

  async getMonthlyStats(month, schoolId = null) {
    const schoolIdToUse = schoolId || getCurrentSchoolId();
    const statsId = `${schoolIdToUse}_${month}`;
    
    const docRef = doc(db, COLLECTIONS.STATS, statsId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  }
};

// Real-time listeners
export const realtimeService = {
  listenToPickupLogs(schoolId, callback) {
    // Don't start listener if no school ID
    if (!schoolId) {
      console.log('Realtime: No school ID provided for pickup logs listener');
      callback([]);
      return () => {}; // Return empty unsubscribe function
    }
    
    const q = query(
      collection(db, COLLECTIONS.PICKUP_LOGS),
      where('school_id', '==', schoolId),
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    
    return onSnapshot(q, (snapshot) => {
      const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(logs);
    });
  },

  listenToGuestNotifications(schoolId, callback) {
    // Don't start listener if no school ID
    if (!schoolId) {
      console.log('Realtime: No school ID provided for guest notifications listener');
      callback([]);
      return () => {}; // Return empty unsubscribe function
    }
    
    const q = query(
      collection(db, COLLECTIONS.GUESTS),
      where('school_id', '==', schoolId),
      where('approval_status', '==', 'pending'),
      orderBy('created_at', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(notifications);
    });
  },

  listenToStudentUpdates(schoolId, callback) {
    // Don't start listener if no school ID
    if (!schoolId) {
      console.log('Realtime: No school ID provided for student updates listener');
      callback([]);
      return () => {}; // Return empty unsubscribe function
    }
    
    const q = query(
      collection(db, COLLECTIONS.STUDENTS),
      where('school_id', '==', schoolId),
      orderBy('name')
    );
    
    return onSnapshot(q, (snapshot) => {
      const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(students);
    });
  }
};

// Settings Service
export const settingsService = {
  async getAll(userId = null) {
    const userIdToUse = userId || getCurrentUserId();
    if (!userIdToUse) {
      console.error('Settings: No user ID available');
      return {};
    }

    try {
      const docRef = doc(db, COLLECTIONS.SETTINGS, userIdToUse);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('Settings: Retrieved settings for user:', userIdToUse);
        return data.settings || {};
      } else {
        console.log('Settings: No settings found for user, returning defaults');
        return this.getDefaultSettings();
      }
    } catch (error) {
      console.error('Settings: Error getting settings:', error);
      return this.getDefaultSettings();
    }
  },

  async getByCategory(userId = null, category) {
    const allSettings = await this.getAll(userId);
    return allSettings[category] || this.getDefaultSettings()[category] || {};
  },

  async save(userId = null, category, settingsData) {
    const userIdToUse = userId || getCurrentUserId();
    if (!userIdToUse) {
      throw new Error('No user ID available for saving settings');
    }

    try {
      const docRef = doc(db, COLLECTIONS.SETTINGS, userIdToUse);
      
      // Get existing settings first
      const existingSettings = await this.getAll(userIdToUse);
      
      // Update the specific category
      const updatedSettings = {
        ...existingSettings,
        [category]: settingsData
      };

      // Save to database
      await updateDoc(docRef, {
        settings: updatedSettings,
        updated_at: serverTimestamp(),
        updated_by: userIdToUse
      }).catch(async (error) => {
        if (error.code === 'not-found') {
          // Document doesn't exist, create it
          await addDoc(collection(db, COLLECTIONS.SETTINGS), {
            id: userIdToUse,
            settings: updatedSettings,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
            created_by: userIdToUse,
            updated_by: userIdToUse
          });
        } else {
          throw error;
        }
      });

      console.log('Settings: Successfully saved settings for category:', category);
      return updatedSettings;
    } catch (error) {
      console.error('Settings: Error saving settings:', error);
      throw error;
    }
  },

  async reset(userId = null, category = null) {
    const userIdToUse = userId || getCurrentUserId();
    if (!userIdToUse) {
      throw new Error('No user ID available for resetting settings');
    }

    try {
      const defaultSettings = this.getDefaultSettings();
      
      if (category) {
        // Reset specific category
        const existingSettings = await this.getAll(userIdToUse);
        const updatedSettings = {
          ...existingSettings,
          [category]: defaultSettings[category]
        };
        
        const docRef = doc(db, COLLECTIONS.SETTINGS, userIdToUse);
        await updateDoc(docRef, {
          settings: updatedSettings,
          updated_at: serverTimestamp(),
          updated_by: userIdToUse
        });
        
        console.log('Settings: Reset category:', category);
        return updatedSettings;
      } else {
        // Reset all settings
        const docRef = doc(db, COLLECTIONS.SETTINGS, userIdToUse);
        await updateDoc(docRef, {
          settings: defaultSettings,
          updated_at: serverTimestamp(),
          updated_by: userIdToUse
        });
        
        console.log('Settings: Reset all settings');
        return defaultSettings;
      }
    } catch (error) {
      console.error('Settings: Error resetting settings:', error);
      throw error;
    }
  },

  getDefaultSettings() {
    return {
      general: {
        schoolName: 'SafeLine Academy',
        timezone: 'America/New_York',
        language: 'en',
        dateFormat: 'MM/DD/YYYY',
        timeFormat: '12h',
        autoLogout: true,
        sessionTimeout: 30,
        enableNotifications: true,
        enableSounds: false,
        enableEmailAlerts: true,
        enableSMSAlerts: false
      },
      security: {
        twoFactorEnabled: false,
        requirePasswordChange: true,
        passwordExpiryDays: 90,
        maxLoginAttempts: 5,
        lockoutDuration: 30,
        enableAuditLog: true,
        sessionTimeout: 30,
        requireStrongPassword: true,
        minPasswordLength: 8
      },
      notifications: {
        enablePushNotifications: true,
        enableEmailNotifications: true,
        enableSMSNotifications: false,
        quietHoursEnabled: false,
        quietHoursStart: '22:00',
        quietHoursEnd: '07:00',
        notificationFrequency: 'immediate',
        pickupNotifications: true,
        guestApprovalNotifications: true,
        emergencyNotifications: true
      },
      school: {
        schoolName: 'SafeLine Academy',
        schoolAddress: '',
        contactEmail: '',
        contactPhone: '',
        operatingHoursStart: '07:00',
        operatingHoursEnd: '18:00',
        pickupWindowStart: '14:30',
        pickupWindowEnd: '16:00',
        enableGuestPickup: true,
        requireGuestPreApproval: true,
        maxGuestsPerFamily: 5
      },
      user: {
        displayName: '',
        email: '',
        phone: '',
        preferredLanguage: 'en',
        timezone: 'America/New_York',
        emailNotifications: true,
        smsNotifications: false,
        theme: 'light'
      }
    };
  }
};

// Export all services
const databaseService = {
  schools: schoolsService,
  users: usersService,
  students: studentsService,
  guardians: guardiansService,
  guests: guestsService,
  pickupLogs: pickupLogsService,
  walkingSchedules: walkingSchedulesService,
  guardianTags: guardianTagsService,
  stats: statsService,
  realtime: realtimeService,
  settings: settingsService
};

export default databaseService;